import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Plus, Loader2, LayoutGrid, List, Grid3X3 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Toggle } from '../ui/toggle';
import SectionHeaderShadcn from './SectionHeader.shadcn';
import { cn } from '../../lib/utils';

/**
 * Recommendations sidebar module - shows card recommendations based on deck
 * shadcn/ui version
 */
function RecommendationsShadcn({
  isCollapsed,
  onToggle,
  recommendations,
  loading,
  onAddCard,
  getProxyImageUrl,
  deckHasCards,
  sidebarExpanded,
  viewMode: externalViewMode,
  t = (key) => null,
  hideHeader = false,
}) {
  // Local view mode state - defaults to 'medium' for grid view
  const [localViewMode, setLocalViewMode] = useState('medium');
  const viewMode = localViewMode;
  const isGridView = viewMode !== 'details';

  return (
    <div className="flex flex-col gap-2 max-w-full overflow-hidden">
      {!hideHeader && (
        <SectionHeaderShadcn
          title={t('sidebar.recommendations.title') || 'Recommended Cards'}
          isCollapsed={isCollapsed}
          onToggle={onToggle}
          t={t}
        />
      )}

      {!isCollapsed && (
        <div className="bg-muted/50 rounded-lg p-3">
          {/* View Mode Toggles */}
          <div className="flex items-center justify-end mb-2">
            <div className="flex items-center border rounded-md">
              <Toggle
                pressed={viewMode === 'details'}
                onPressedChange={() => setLocalViewMode('details')}
                size="sm"
                className="rounded-r-none data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                title={t('sidebar.search.listView') || 'List view'}
              >
                <List className="h-4 w-4" />
              </Toggle>
              <Toggle
                pressed={viewMode === 'large'}
                onPressedChange={() => setLocalViewMode('large')}
                size="sm"
                className="rounded-none border-x data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                title={t('sidebar.search.largeGrid') || 'Large grid'}
              >
                <LayoutGrid className="h-4 w-4" />
              </Toggle>
              <Toggle
                pressed={viewMode === 'medium'}
                onPressedChange={() => setLocalViewMode('medium')}
                size="sm"
                className="rounded-l-none data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                title={t('sidebar.search.mediumGrid') || 'Medium grid'}
              >
                <Grid3X3 className="h-4 w-4" />
              </Toggle>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-muted-foreground">
                {t('sidebar.recommendations.loading') || 'Loading recommendations...'}
              </span>
            </div>
          )}

          {/* No Recommendations State */}
          {!loading && recommendations.length === 0 && deckHasCards && (
            <p className="text-center text-muted-foreground py-4 italic text-sm">
              {t('sidebar.recommendations.noRecommendations') || 'No recommendations found for your deck'}
            </p>
          )}

          {/* Recommendations List */}
          {!loading && recommendations.length > 0 && (
            <ScrollArea className="h-[300px]">
              <div
                className={cn(
                  viewMode === 'details'
                    ? 'flex flex-col gap-2'
                    : viewMode === 'large'
                    ? 'grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2'
                    : 'grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-2'
                )}
              >
                {recommendations.map((card, index) => {
                  const isScene = card.feature?.value === 'scene' || card.cardType === 'Scene';

                  return (
                    <div
                      key={index}
                      onClick={() => onAddCard(card)}
                      className={cn(
                        'flex cursor-pointer rounded-md bg-card transition-colors hover:bg-accent p-2',
                        isGridView
                          ? 'flex-col items-center text-center min-h-[160px]'
                          : 'items-center gap-3'
                      )}
                    >
                      <img
                        src={getProxyImageUrl(card.images?.blob?.thumbnail || card.images?.original?.thumbnail)}
                        alt={card.name}
                        className={cn(
                          'object-cover rounded',
                          isGridView
                            ? isScene ? 'w-full aspect-[88/63]' : 'w-full aspect-[63/88]'
                            : 'w-12 h-[67px]'
                        )}
                        loading="lazy"
                      />
                      {!isGridView && (
                        <>
                          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                            <span className="font-semibold truncate text-sm">
                              {card.name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {card.number}
                            </span>
                            <div className="flex gap-1 flex-wrap">
                              {card.characterName && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0">
                                  {card.characterName}
                                </Badge>
                              )}
                              {card.rarity?.description && (
                                <Badge variant="secondary" className="text-[10px] px-1 py-0">
                                  {card.rarity.description}
                                </Badge>
                              )}
                            </div>
                            <span className="text-xs font-semibold text-primary">
                              {t('sidebar.recommendations.score') || 'Score'}: {card.recommendationScore?.toFixed(1)}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            title={t('common.add') || 'Add'}
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddCard(card);
                            }}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </div>
      )}
    </div>
  );
}

RecommendationsShadcn.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  recommendations: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  onAddCard: PropTypes.func.isRequired,
  getProxyImageUrl: PropTypes.func.isRequired,
  deckHasCards: PropTypes.bool.isRequired,
  sidebarExpanded: PropTypes.bool.isRequired,
  viewMode: PropTypes.string.isRequired,
  t: PropTypes.func,
  hideHeader: PropTypes.bool,
};

export default RecommendationsShadcn;
